import { Link } from "react-router-dom";

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 text-base font-semibold text-ink">{heading}</h2>
      <div className="space-y-4 text-sm leading-relaxed text-ink-secondary">{children}</div>
    </section>
  );
}

const DOMAINS = [
  "Licensing & Compliance",
  "Subsidy & Funding",
  "Workforce Development",
  "Health, Safety & Nutrition",
  "Early Learning Standards",
  "Family Engagement",
  "Data & Technology",
  "Special Populations",
];

export default function About() {
  return (
    <div className="mx-auto max-w-2xl animate-fade-in px-6 py-12">

      {/* Hero */}
      <header className="mb-12">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border-subtle bg-surface-raised px-3 py-1">
          <span className="text-2xs font-semibold uppercase tracking-widest text-ink-tertiary">LegiSignal</span>
          <span className="text-ink-disabled" aria-hidden>·</span>
          <span className="text-2xs font-semibold uppercase tracking-widest text-primary">DECAL Pilot</span>
        </div>
        <h1 className="mb-4 text-3xl font-semibold tracking-tight text-ink">About this tool</h1>
        <p className="text-base leading-relaxed text-ink-secondary">
          LegiSignal monitors the Georgia General Assembly and surfaces bills that
          matter to early care and learning — before they require a reaction.
        </p>
      </header>

      {/* Sections */}
      <Section heading="What LegiSignal does">
        <p>
          LegiSignal is a legislative intelligence tool built specifically for the
          Georgia Department of Early Care and Learning (DECAL). It continuously
          monitors every bill introduced in the Georgia General Assembly, identifies
          those relevant to DECAL's eight policy domains, and enriches them with
          AI-powered analysis.
        </p>
        <p>
          When a bill crosses an impact threshold — based on its risk level, urgency,
          scope of program areas affected, and operational complexity — LegiSignal
          generates a <strong className="font-medium text-ink">signal</strong>: a
          concise, actionable brief for DECAL leadership. Signals are ranked by impact
          score so the Commissioner sees the most consequential bills first.
        </p>
        <p>
          The tool does not replace legislative counsel or policy staff. It is designed
          to make their work faster by surfacing what needs attention, when it needs it.
        </p>
      </Section>

      <Section heading="Data source">
        <p>
          All legislative data is sourced from{" "}
          <a href="https://legiscan.com" target="_blank" rel="noopener noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary">
            LegiScan
          </a>
          , a nonpartisan legislative tracking service covering all 50 state legislatures.
          Bill text, status, history, and committee assignments are retrieved via the
          LegiScan API and refreshed automatically throughout the legislative session.
        </p>
        <p>
          Legislative data is used under the{" "}
          <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary">
            Creative Commons Attribution 4.0 International license (CC BY 4.0)
          </a>.
        </p>
        <p>
          AI analysis — including the commissioner summary, program area identification,
          operational impact assessment, and scoring rationale — is generated by
          Azure OpenAI (GPT-4.1).
        </p>
      </Section>

      <Section heading="Pilot scope and limitations">
        <p>
          This is an internal DECAL pilot. LegiSignal is a{" "}
          <strong className="font-medium text-ink">research and monitoring tool</strong>,
          not a source of legal, regulatory, or policy advice. Impact scores and signal
          classifications are generated by an AI model and are intended to help staff{" "}
          <em>prioritize</em> legislative review — not to substitute for professional
          legal judgment.
        </p>
        <p>
          DECAL staff should independently verify any bill's current status, text, and
          legislative history, and consult General Counsel before taking action in
          response to any signal.
        </p>
        <p>
          Coverage is currently limited to the Georgia General Assembly. The system
          tracks all bills that match DECAL's eight policy domains using keyword-based
          tagging and AI relevance scoring.
        </p>
      </Section>

      {/* Policy domains */}
      <section className="mb-10">
        <h2 className="mb-4 text-base font-semibold text-ink">DECAL policy domains monitored</h2>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {DOMAINS.map((domain) => (
            <li key={domain}
              className="flex items-center gap-2.5 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 text-sm font-medium text-ink-secondary">
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
              {domain}
            </li>
          ))}
        </ul>
      </section>

      {/* Back link */}
      <div className="border-t border-border-subtle pt-8">
        <Link to="/"
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-ink-secondary transition-colors hover:text-primary focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary">
          <svg className="h-4 w-4 transition-transform duration-150 group-hover:-translate-x-0.5"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Today's Signals
        </Link>
      </div>

    </div>
  );
}
